<template>
  <v-card outlined class="delivery-summary-card" @click="openDeliveryList">
    <v-card-text class="pa-4">
      <div class="delivery-header">
        <div>
          <div class="section-title">Deliveries</div>
          <div class="section-subtitle">Next 15 days summary</div>
        </div>

        <v-avatar color="blue lighten-5" size="42">
          <v-icon color="blue darken-2">mdi-truck-delivery-outline</v-icon>
        </v-avatar>
      </div>

      <v-skeleton-loader
        v-if="loading"
        type="list-item-two-line"
        class="mt-4"
      />

      <div v-else>
        <v-row dense class="mt-4">
          <v-col cols="4">
            <div class="summary-box">
              <div class="summary-value">{{ totalDeliveries }}</div>
              <div class="summary-label">Total</div>
            </div>
          </v-col>

          <v-col cols="4">
            <div class="summary-box">
              <div class="summary-value pending-text">{{ pendingCount }}</div>
              <div class="summary-label">Pending</div>
            </div>
          </v-col>

          <v-col cols="4">
            <div class="summary-box">
              <div class="summary-value delivered-text">{{ deliveredCount }}</div>
              <div class="summary-label">Delivered</div>
            </div>
          </v-col>
        </v-row>

        <div class="today-box mt-4">
          <div>
            <div class="today-label">Today</div>
            <div class="today-value">
              {{ todayDeliveries.length }} delivery{{ todayDeliveries.length === 1 ? "" : "ies" }}
            </div>
          </div>

          <v-btn small text color="primary">
            View All
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <div v-if="todayDeliveries.length > 0" class="today-list mt-3">
          <div
            v-for="item in todayDeliveries.slice(0, 3)"
            :key="item.id"
            class="today-row"
          >
            <div>
              <div class="delivery-name">{{ item.name }}</div>
              <div class="delivery-time">
                {{ formatTime(item.startTime) }} - {{ formatTime(item.endTime) }}
              </div>
            </div>

            <v-chip
              small
              :color="item.status === 'pending' ? 'orange lighten-5' : 'green lighten-5'"
              :text-color="item.status === 'pending' ? 'orange darken-3' : 'green darken-3'"
            >
              {{ item.status }}
            </v-chip>
          </div>
        </div>

        <div v-else class="empty-message mt-3">
          No delivery scheduled for today.
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import deliveryService from "@/modules/Delivery/service.js";

export default {
  name: "DeliveryCalendar",

  data() {
    return {
      loading: true,
      deliveries: [],
    };
  },

  computed: {
    totalDeliveries() {
      return this.deliveries.length;
    },

    pendingCount() {
      return this.deliveries.filter((item) => item.status === "pending").length;
    },

    deliveredCount() {
      return this.deliveries.filter((item) => item.status === "delivered").length;
    },

    todayDeliveries() {
      const today = this.$moment().format("YYYY-MM-DD");

      return this.deliveries.filter((item) => {
        return this.$moment.utc(item.deliveryDate).format("YYYY-MM-DD") === today;
      });
    },
  },

  mounted() {
    this.getDeliveries();
  },

  methods: {
    openDeliveryList() {
      this.$router.push({ path: "/Delivery/list" });
    },

    formatTime(time) {
      if (!time) {
        return "Not Set";
      }

      return this.$moment(time, "HH:mm").format("hh:mm A");
    },

    async getDeliveries() {
      this.loading = true;

      const where = {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().add(15, "days").endOf("day").toISOString(),
        status: null,
      };

      try {
        const response = await deliveryService.getAll(where);
        this.deliveries = response.data || [];
      } catch (error) {
        console.log("delivery summary failed", error);
        this.deliveries = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.delivery-summary-card {
  border-radius: 20px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delivery-summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.delivery-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 12px;
  text-align: center;
}

.summary-value {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.pending-text {
  color: #ea580c;
}

.delivered-text {
  color: #16a34a;
}

.today-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.today-label {
  font-size: 12px;
  color: #64748b;
}

.today-value {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.today-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.today-row {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.delivery-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.delivery-time {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.empty-message {
  font-size: 13px;
  color: #64748b;
  text-align: center;
  background: #f8fafc;
  border-radius: 14px;
  padding: 14px;
}
</style>